import { RefObject, useEffect } from 'react'

const isScrollableElement = (element: HTMLElement) => {
  const style = window.getComputedStyle(element)

  return (
    /(auto|scroll|overlay)/.test(style.overflowY) ||
    /(auto|scroll|overlay)/.test(style.overflowX) ||
    /(auto|scroll|overlay)/.test(style.overflow)
  )
}

export const getScrollableParents = (element: HTMLElement | null) => {
  const parents: HTMLElement[] = []
  let current = element?.parentElement

  while (current) {
    if (isScrollableElement(current)) {
      parents.push(current)
    }
    current = current.parentElement
  }

  return parents
}

export const useCloseOnAncestorScroll = (
  anchorRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  onClose: () => void,
  ignoreRef?: RefObject<HTMLElement | null>,
) => {
  useEffect(() => {
    if (!enabled) return

    const anchor = anchorRef.current
    if (!anchor) return

    const parents = getScrollableParents(anchor)

    const handleScroll = (event: Event) => {
      const target = event.target
      const ignoreRoot = ignoreRef?.current

      if (
        ignoreRoot &&
        target instanceof Node &&
        ignoreRoot.contains(target)
      ) {
        return
      }

      // window + capture слышит скролл любых потомков (в т.ч. порталы).
      // Закрываем только если реально скроллится предок якоря / документ.
      if (
        target === document ||
        target === document.documentElement ||
        target === document.body
      ) {
        onClose()
        return
      }

      if (target instanceof HTMLElement && parents.includes(target)) {
        onClose()
      }
    }

    parents.forEach((parent) => {
      parent.addEventListener('scroll', handleScroll, {
        passive: true,
        capture: true,
      })
    })
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    })
    window.addEventListener('resize', onClose, { passive: true })

    return () => {
      parents.forEach((parent) => {
        parent.removeEventListener('scroll', handleScroll, { capture: true })
      })
      window.removeEventListener('scroll', handleScroll, { capture: true })
      window.removeEventListener('resize', onClose)
    }
  }, [anchorRef, enabled, ignoreRef, onClose])
}
